import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

/**
 * SEO helper — dynamic metadata + JSON-LD Schema.org + hreflang.
 */
export default function SEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  article,
  schema,
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const site = typeof window !== "undefined" ? window.location.origin : "https://bhtsolutions.com";
  const url = `${site}/${lang}${path}`;
  const fullTitle = title ? `${title} — BHT Solutions` : (lang === "nl" ? "BHT Solutions — De digitale hotelmanager" : "BHT Solutions — The digital hotel manager");
  const desc =
    description ||
    (lang === "nl"
      ? "BHT One is de digitale hotelmanager die uw medewerkers, kennis en dagelijkse hotelprocessen samenbrengt in één centrale werkomgeving — naast uw bestaande PMS. Voor familiehotels én hotelgroepen."
      : "BHT One is the digital hotel manager that brings your people, knowledge and daily operation into one central workspace — alongside your existing PMS. For family-run hotels and hotel groups alike.");
  const ogImage = image || `${site}/og-default.jpg`;

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BHT Solutions",
    url: site,
    logo: `${site}/logo.svg`,
    sameAs: [],
    description: desc,
  };
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "BHT Solutions",
    url: site,
    potentialAction: {
      "@type": "SearchAction",
      target: `${site}/${lang}/knowledge?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  const softwareApplication = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "BHT One",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: lang === "nl"
      ? "De digitale hotelmanager die uw hele hotel samenbrengt op één plek — naast uw bestaande PMS."
      : "The digital hotel manager that brings your whole hotel together in one workspace — alongside your existing PMS.",
    offers: { "@type": "Offer", availability: "https://schema.org/InStock" },
  };

  const graph = [organization, website, softwareApplication];
  if (article) graph.push({ "@context": "https://schema.org", "@type": "Article", ...article });
  if (schema) graph.push(schema);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en" href={`${site}/en${path}`} />
      <link rel="alternate" hrefLang="nl" href={`${site}/nl${path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${site}/en${path}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="BHT Solutions" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === "nl" ? "nl_NL" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="theme-color" content="#050505" />

      <script type="application/ld+json">{JSON.stringify({ "@graph": graph })}</script>
    </Helmet>
  );
}
